import { CallIntelligenceService } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';
import { CommHistoryService } from '@weave/schema-gen-ts/dist/schemas/comm/history/v1/history_service.pb';
import {
  InsuranceResponse,
  InsuranceVerificationAdapterService,
  InsuranceVerificationDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { PersonService } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons_service.pb';
import {
  DataSource,
  GetDataSourceBySourceIDResponse,
} from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { IntegrationsService } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integrations_service.pb';
import { MockPersons } from '@frontend/api-person';
import { FeatureMockRouter, DemoIdUtils, DemoInterceptorUtils } from '@frontend/demo-interceptors';
import { MockAPIRouter } from '@frontend/fetch';
import { genUUIDV4 } from '@frontend/string';
import { serviceName } from '../../service';
import * as CallIntelTypes from '../../types';
import { BaseMockRouterContext } from './types';

export interface ContactPanelMockRouterContext extends BaseMockRouterContext {
  mockPersonsInstance: MockPersons;
}

export const ContactPanelMockRouter: FeatureMockRouter<ContactPanelMockRouterContext> = {
  addMockRouterEndpoints: (router, { mockPersonsInstance }) => {
    router.get('/photo/v1/person/:personId', async (_, params, next) => {
      if (DemoIdUtils.checkIsDemoId(params.personId ?? '')) {
        return MockAPIRouter.json({ error: 'Problem finding image file' }, 404);
      }
      next();
      return null;
    });

    router.get('/desktop/v1/person_extended/:personId', async (_, params, next) => {
      const { personId } = params;
      if (DemoIdUtils.checkIsDemoId(personId ?? '')) {
        const personExtendedResponse = mockPersonsInstance.getPersonExtended({ personId });
        return MockAPIRouter.json({ data: personExtendedResponse });
      }
      next();
      return null;
    });

    router.schemaEndpoint(PersonService.GetPersonLegacy, async (_, params, next) => {
      const { personId } = params;

      if (DemoIdUtils.checkIsDemoId(personId ?? '')) {
        const personData = mockPersonsInstance.getPersonV3({ personId });
        return MockAPIRouter.json({ data: personData });
      }
      next();
      return null;
    });

    router.schemaEndpoint(PersonService.SearchPersonsLegacy, async (request, _, next) => {
      const { searchParams } = new URL(request.url);
      const personId = searchParams.get('personId') ?? '';
      const householdId = searchParams.get('householdId') ?? '';

      if (DemoIdUtils.checkIsDemoId(personId) || DemoIdUtils.checkIsDemoId(householdId)) {
        const persons = mockPersonsInstance.getFilteredPersons({ personId, householdId });
        return MockAPIRouter.json({ persons, nextPageToken: '' });
      }
      next();
      return null;
    });

    router.schemaEndpoint(InsuranceVerificationAdapterService.GetInsuranceByPersonID, async (_, params, next) => {
      if (DemoIdUtils.checkIsDemoId(params.personId ?? '')) {
        return MockAPIRouter.json({} as InsuranceResponse);
      }
      next();
      return null;
    });

    router.schemaEndpoint(
      InsuranceVerificationAdapterService.GetInsuranceVerificationDetails,
      async (request, _, next) => {
        const { searchParams } = new URL(request.url);
        const personId = searchParams.get('personId') ?? '';
        if (DemoIdUtils.checkIsDemoId(personId)) {
          return MockAPIRouter.json({
            coverage: {},
            memberId: '',
            onederfulPayerId: '',
          } as InsuranceVerificationDetailsResponse);
        }
        next();
        return null;
      }
    );

    router.get('/task/:householdId/set', async (_, params, next) => {
      if (DemoIdUtils.checkIsDemoId(params.householdId ?? '')) {
        return MockAPIRouter.json({
          birthdays: [],
          reminders: [],
          contact: [],
          overdue: [],
          collections: [],
          information: undefined,
          eyewear: undefined,
        });
      }
      next();
      return null;
    });

    router.schemaEndpoint(IntegrationsService.GetDataSourceBySourceID, async (_, params, next) => {
      if (DemoIdUtils.checkIsDemoId(params.sourceId ?? '')) {
        return MockAPIRouter.json({
          dataSource: undefined,
        } as GetDataSourceBySourceIDResponse);
      }
      next();
      return null;
    });

    router.schemaEndpoint(IntegrationsService.GetDataSource, async (_, params, next) => {
      if (DemoIdUtils.checkIsDemoId(params.sourceId ?? '')) {
        return MockAPIRouter.json({
          integration: {},
        } as DataSource);
      }
      next();
      return null;
    });

    router.get('/notes/', async (request, _, next) => {
      // url : /notes/?filter=household_id~demo-c3565210-b2d7-4429-87e1-439d50d2ddb5
      const { searchParams } = new URL(request.url);
      if (searchParams.get('filter')?.includes('household_id~demo')) {
        return MockAPIRouter.json([]);
      }
      next();
      return null;
    });

    router.get(/.*\/v1\/search\/invoices.*/, async (request, _, next) => {
      const { searchParams } = new URL(request.url);
      const personId = searchParams.get('personid') ?? '';
      if (DemoIdUtils.checkIsDemoId(personId)) {
        return MockAPIRouter.json({});
      }
      next();
      return null;
    });
  },
  getRequestInterceptors: ({ interceptorIdPrefix, mockPersonsInstance, vertical, hasVoiceAiAccess }) => [
    {
      id: `${interceptorIdPrefix}-person-recent-call`,
      interceptor: async (request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: CallIntelligenceService.RecentCalls,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody: CallIntelTypes.RecentCallsIO['input'] = await request.clone().json();

        if (!requestBody?.personId || !DemoIdUtils.checkIsDemoId(requestBody.personId)) {
          return request;
        }

        const personData = mockPersonsInstance.getFilteredPersons({
          personId: requestBody.personId,
        })?.[0];

        if (!personData) {
          return request;
        }

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            isDemo: true,
            demoMetadata: {
              firstName: personData.firstName,
              lastName: personData.lastName,
              gender: personData.gender,
              vertical,
              features: {
                enableCallIntelligence: true,
                enableVoiceAi: hasVoiceAiAccess,
              },
              enableDemoCallPool: true,
            },
            personId: DemoIdUtils.removeDemoDataFromId(personData.personId),
          } as CallIntelTypes.RecentCallsIO['input']),
        });
      },
    },
    {
      id: `${interceptorIdPrefix}-call-trends`,
      interceptor: async (request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: CallIntelligenceService.GetCallsOverview,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody: CallIntelTypes.GetCallsOverviewIO['input'] = await request.clone().json();

        // If overview call is not made by call trends section pass it to mock interceptor
        if (requestBody.isCallTrendDemo === undefined) {
          return request;
        }

        const { personId: personIdList } =
          requestBody.filter?.[CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID] ?? {};

        if (
          requestBody.filter &&
          requestBody.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID] &&
          personIdList
        ) {
          requestBody.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID].personId = personIdList.map((id) =>
            DemoIdUtils.removeDemoDataFromId(id)
          );
        }

        requestBody.demoMetadata = {
          ...requestBody.demoMetadata,
          vertical,
          features: {
            enableCallIntelligence: true,
            enableVoiceAi: hasVoiceAiAccess,
          },
        };

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            isDemo: false,
            isCallTrendDemo: true,
          } as CallIntelTypes.GetCallsOverviewIO['input']),
        });
      },
    },
    {
      id: `${interceptorIdPrefix}-comm-history`,
      interceptor: async (request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: CommHistoryService.ListHistory,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody = await request.clone().json();
        const personId = requestBody?.personId;

        if (!personId || !DemoIdUtils.checkIsDemoId(personId)) return request;

        const personData = mockPersonsInstance.getFilteredPersons({
          personId,
        })?.[0];

        if (!personData) {
          return request;
        }

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            personId: DemoIdUtils.removeDemoDataFromId(personId),
            isDemo: true,
            demoMetadata: {
              ...requestBody?.demoMetadata,
              invokedBy: 'INVALID_SOURCE',
              firstName: personData.firstName || 'Jane',
              lastName: personData.lastName || 'Doe',
              gender: personData.gender || 'Female',
              vertical,
              callId: requestBody?.demoMetadata?.callId || genUUIDV4(),
              features: {
                enableCallIntelligence: true,
                enableVoiceAi: hasVoiceAiAccess,
              },
              enableDemoCallPool: true,
            },
          }),
        });
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([serviceName, 'GetCallsOverview']);
    // Other queries related to contact panel have 'demo' key checked in request so they do not need to be invalidated
  },
};
